import { useState } from "react";
import {
  Box,
  Heading,
  Text,
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalCloseButton,
  HStack,
  PinInput,
  PinInputField,
  useToast,
} from "@chakra-ui/react";

import { resendEmailOtpApi, verifyOtpApi } from "../services/auth";

interface EmailVerificationModalProps {
  isOpen: boolean;
  onClose: () => void;
  email: string;
  token: string;
  onVerified: () => void;
}

export const EmailVerificationModal: React.FC<
  EmailVerificationModalProps
> = ({ isOpen, onClose, email, token, onVerified }) => {
  const toast = useToast();

  const [otp, setOtp] = useState<string>("");
  const [verifying, setVerifying] = useState<boolean>(false);
  const [resending, setResending] = useState<boolean>(false);

  const handleVerify = async () => {
    if (otp.length !== 4) return;

    setVerifying(true);

    const res = await verifyOtpApi({ email, otp }, token);

    if (res && !res.error) {
      toast({
        title: "Email verified",
        description: "Your email has been verified successfully",
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });

      onVerified();
    } else {
      toast({
        title: "Verification failed",
        description:
          res?.message || "The code you entered is incorrect. Please try again.",
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
    }

    setVerifying(false);
  };

  const handleResend = async () => {
    setResending(true);

    const res = await resendEmailOtpApi(email, token);

    if (res && !res.error) {
      toast({
        title: "Code sent",
        description: `A new verification code has been sent to ${email}`,
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
    } else {
      toast({
        title: "An error occurred",
        description: res?.message || "Could not resend the code. Please try again.",
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
    }

    setResending(false);
  };

  return (
    <Modal isCentered isOpen={isOpen} onClose={onClose} size={"xs"}>
      <ModalOverlay bg="#999" backdropFilter="blur(10px)" />

      <ModalContent
        borderRadius={"20px"}
        pt="32px"
        pb="24px"
        px="24px"
        mx="20px"
      >
        <ModalCloseButton />

        <Heading
          fontSize={"20px"}
          color={"#19202D"}
          textAlign={"center"}
          fontFamily={"RedHatDisplay-Bold"}
          mb="8px"
        >
          Verify Your Email
        </Heading>

        <Text
          fontSize={"14px"}
          color={"#4F4F51"}
          textAlign={"center"}
          mb="24px"
        >
          Enter the 4-digit code sent to{" "}
          <Text as="span" fontWeight={700} color={"#19202D"}>
            {email}
          </Text>
        </Text>

        <HStack justify={"center"} spacing={"12px"} mb="24px">
          <PinInput
            otp
            value={otp}
            onChange={setOtp}
            onComplete={handleVerify}
            placeholder=""
          >
            <PinInputField
              boxShadow="0px 2px 5px rgba(0, 0, 0, 0.1)"
              borderWidth={0}
              h={{ base: "48px", md: "56px" }}
              w={{ base: "48px", md: "56px" }}
              fontSize={"20px"}
              borderRadius={"12px"}
              _focusVisible={{ boxShadow: "0px 0px 0px 2px #FF8E3C" }}
            />
            <PinInputField
              boxShadow="0px 2px 5px rgba(0, 0, 0, 0.1)"
              borderWidth={0}
              h={{ base: "48px", md: "56px" }}
              w={{ base: "48px", md: "56px" }}
              fontSize={"20px"}
              borderRadius={"12px"}
              _focusVisible={{ boxShadow: "0px 0px 0px 2px #FF8E3C" }}
            />
            <PinInputField
              boxShadow="0px 2px 5px rgba(0, 0, 0, 0.1)"
              borderWidth={0}
              h={{ base: "48px", md: "56px" }}
              w={{ base: "48px", md: "56px" }}
              fontSize={"20px"}
              borderRadius={"12px"}
              _focusVisible={{ boxShadow: "0px 0px 0px 2px #FF8E3C" }}
            />
            <PinInputField
              boxShadow="0px 2px 5px rgba(0, 0, 0, 0.1)"
              borderWidth={0}
              h={{ base: "48px", md: "56px" }}
              w={{ base: "48px", md: "56px" }}
              fontSize={"20px"}
              borderRadius={"12px"}
              _focusVisible={{ boxShadow: "0px 0px 0px 2px #FF8E3C" }}
            />
          </PinInput>
        </HStack>

        <Button
          bg={"#FF8E3C"}
          _hover={{ bg: "#FF8E3C" }}
          borderRadius={"30px"}
          color={"white"}
          h="52px"
          fontSize={"16px"}
          fontFamily={"RedHatDisplay-Bold"}
          onClick={handleVerify}
          disabled={otp.length !== 4 || verifying}
          isLoading={verifying}
          loadingText={"Verifying"}
          mb="16px"
        >
          Verify
        </Button>

        <Box textAlign={"center"}>
          <Text
            as="span"
            fontSize={"14px"}
            color={"#FF762C"}
            cursor={resending ? "default" : "pointer"}
            opacity={resending ? 0.6 : 1}
            onClick={resending ? undefined : handleResend}
          >
            {resending ? "Resending..." : "Resend Code"}
          </Text>
        </Box>
      </ModalContent>
    </Modal>
  );
};
